import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useAuth, useIsAuthenticated } from "../hooks/useAuth";
import { useAgent, useAgentThreads } from "../hooks/useAgent";
import { useAgentRuntimeStatus, useEnsureAgentRuntime } from "../hooks/useAgentRuntime";
import { useBrand } from "../hooks/useBrand";
import { useUploadMedia } from "../hooks/useMedia";
import { RuntimeStatusBadge } from "../components/agent/RuntimeStatusBadge";
import { AgentUIComposer, type AgentComposerUploadItem } from "../components/agent-v2/AgentUIComposer";
import { AgentUIHistoryPanel } from "../components/agent-v2/AgentUIHistoryPanel";
import { AgentUIMessageStream } from "../components/agent-v2/AgentUIMessageStream";
import { BrandReadinessBanner } from "../components/brand/BrandReadinessBanner";
import { WorkspaceContentFrame } from "../components/workspace/WorkspaceContentFrame";
import { api } from "../lib/api";
import { timeAgo } from "../lib/utils";

const ACCEPTED_CHAT_UPLOAD_MIME_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);

type PendingAttachment = {
  id: string;
  label: string;
  url: string | null;
};

type InflightAttachmentEntry = {
  attachments: PendingAttachment[];
  recoverable: boolean;
};

type UploadQueueItem = AgentComposerUploadItem;

// Fresh V2 page boundary for the dedicated chat-first HelloConvo route.
// This slice intentionally uses the existing live thread/create/send contracts
// before extracting broader shell primitives from a real stateful page.
export function AgentUIPage() {
  const { shellOwnsChatPanel = false } = useOutletContext<{ shellOwnsChatPanel?: boolean }>() ?? {};
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const { loading } = useAuth();
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [briefContext, setBriefContext] = useState<{ id: string; title?: string } | null>(null);
  const [routeError, setRouteError] = useState<string | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isAttaching, setIsAttaching] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);
  const inflightAttachmentsByThreadRef = useRef<Record<string, InflightAttachmentEntry>>({});
  const attachmentThreadRef = useRef<string | null>(null);
  const attachmentSessionTokenRef = useRef(0);
  const activeThreadIdRef = useRef<string | null>(null);
  const brandIdRef = useRef<string | undefined>(brandId);
  const pendingAttachmentsRef = useRef<PendingAttachment[]>([]);
  const initialRouteIntentHandled = useRef("");
  const newThreadRouteIntentClaimRef = useRef("");
  const newThreadCreationsByBrandRef = useRef(new Map<string, symbol>());
  // The thread this page most recently created. createThread resolves before the thread list
  // refetches, so route resolution must not treat this id as missing from `threads`.
  const createdThreadIdRef = useRef<string | null>(null);
  const lastEnsuredBrandId = useRef<string | null>(null);
  const scrollViewportRef = useRef<HTMLDivElement | null>(null);
  const scrollBottomRef = useRef<HTMLDivElement | null>(null);
  const shouldAutoScrollRef = useRef(true);
  const autoScrollLockRef = useRef(true);
  const suppressScrollObserverRef = useRef(false);
  const autoScrollFrameRef = useRef<number | null>(null);
  const isStreamingRef = useRef(false);
  const stopSessionRef = useRef<(() => void) | null>(null);
  const previousBrandIdRef = useRef<string | undefined>(brandId);
  const requestedThreadId = searchParams.get("thread");
  const shouldCreateNewThread = searchParams.get("new") === "1";
  const briefIdFromUrl = searchParams.get("briefId");
  const routeIntentKey = shouldCreateNewThread
    ? `new:${brandId ?? ""}`
    : requestedThreadId
      ? `thread:${brandId ?? ""}:${requestedThreadId}`
      : briefIdFromUrl
        ? `brief:${brandId ?? ""}:${briefIdFromUrl}`
      : `default:${brandId ?? ""}`;

  const { threads, isLoading: threadsLoading, createThread, deleteThread, archivingThreadId } = useAgentThreads(brandId ?? null);
  const {
    activeTool,
    activityStatus,
    clearError,
    error,
    brandNotReadyMissing,
    completedTurnCount,
    isStopping,
    isStreaming,
    isBrandStreamBusy,
    lastCompletedThreadId,
    messages,
    retryLastMessage,
    sendMessage,
    sessionStatus,
    stopSession,
    streamingText,
    synthesisLearned,
    toolProgress,
  } = useAgent(activeThreadId, brandId ?? null);
  const { data: brandData } = useBrand(brandId);
  const uploadMedia = useUploadMedia(brandId ?? "");
  const runtimeStatusQuery = useAgentRuntimeStatus(brandId);
  const { mutateAsync: ensureRuntime, isPending: isEnsuringRuntime } = useEnsureAgentRuntime();

  const brandName = brandData?.data?.name ?? "your brand";

  function getInflightAttachmentEntry(threadId: string | null) {
    if (!threadId) return null;
    return inflightAttachmentsByThreadRef.current[threadId] ?? null;
  }

  function getInflightAttachments(threadId: string | null) {
    return getInflightAttachmentEntry(threadId)?.attachments ?? [];
  }

  function isCurrentNewThreadCreation(brandId: string, creationToken: symbol) {
    return brandIdRef.current === brandId
      && newThreadCreationsByBrandRef.current.get(brandId) === creationToken;
  }

  function setInflightAttachments(threadId: string | null, attachments: PendingAttachment[], recoverable = false) {
    if (!threadId) return;
    inflightAttachmentsByThreadRef.current[threadId] = { attachments, recoverable };
  }

  function markInflightAttachmentsRecoverable(threadId: string | null) {
    if (!threadId) return;
    const current = inflightAttachmentsByThreadRef.current[threadId];
    if (!current) return;
    inflightAttachmentsByThreadRef.current[threadId] = { ...current, recoverable: true };
  }

  function clearInflightAttachments(threadId: string | null) {
    if (!threadId) return;
    delete inflightAttachmentsByThreadRef.current[threadId];
  }

  function preserveAndStopActiveThread(options?: { recoverable?: boolean }) {
    const currentThreadId = activeThreadIdRef.current;
    if (!currentThreadId || !isStreamingRef.current) {
      return;
    }

    if (options?.recoverable && getInflightAttachments(currentThreadId).length > 0) {
      markInflightAttachmentsRecoverable(currentThreadId);
    }

    stopSessionRef.current?.();
  }

  function invalidateAttachmentSession() {
    setAttachmentError(null);
    setIsAttaching(false);
    setPendingAttachments([]);
    setUploadQueue([]);
    attachmentThreadRef.current = null;
    attachmentSessionTokenRef.current += 1;
  }

  function beginThreadTransition(options?: { recoverable?: boolean }) {
    preserveAndStopActiveThread(options);
    invalidateAttachmentSession();
  }

  function restoreThreadTransitionFailure(previousThreadId: string | null, previousPendingAttachments: PendingAttachment[]) {
    const recoveredAttachments = getInflightAttachments(previousThreadId);
    setPendingAttachments(recoveredAttachments.length > 0 ? recoveredAttachments : previousPendingAttachments);
    if (recoveredAttachments.length > 0) {
      clearInflightAttachments(previousThreadId);
    }
    attachmentThreadRef.current = previousThreadId;
  }

  function scrollToLatest(behavior: ScrollBehavior = "auto") {
    const viewport = scrollViewportRef.current;
    if (viewport) {
      suppressScrollObserverRef.current = true;
      viewport.scrollTo({ top: viewport.scrollHeight, behavior });
      requestAnimationFrame(() => {
        suppressScrollObserverRef.current = false;
      });
      return;
    }
    scrollBottomRef.current?.scrollIntoView({ block: "end", behavior });
  }

  function cancelAutoScrollFrame() {
    if (autoScrollFrameRef.current === null) return;
    cancelAnimationFrame(autoScrollFrameRef.current);
    autoScrollFrameRef.current = null;
  }

  function armAutoScroll(behavior: ScrollBehavior = "auto", frames = 1) {
    shouldAutoScrollRef.current = true;
    cancelAutoScrollFrame();
    const tick = (remainingFrames: number) => {
      autoScrollFrameRef.current = requestAnimationFrame(() => {
        if (!shouldAutoScrollRef.current && !autoScrollLockRef.current) {
          autoScrollFrameRef.current = null;
          return;
        }
        if (remainingFrames <= 0) {
          autoScrollFrameRef.current = null;
          scrollToLatest(behavior);
          return;
        }
        tick(remainingFrames - 1);
      });
    };
    tick(frames);
  }

  // Event handlers read these "latest value" refs synchronously. Sync them in the layout
  // phase, not a passive effect: a passive effect runs in a later scheduler task, so a click
  // that lands after the DOM shows new state but before that task would read the previous
  // value (e.g. New thread snapshotting an empty attachment list, then "restoring" nothing
  // when creation fails).
  useLayoutEffect(() => {
    brandIdRef.current = brandId;
  }, [brandId]);

  useLayoutEffect(() => {
    activeThreadIdRef.current = activeThreadId;
  }, [activeThreadId]);

  useLayoutEffect(() => {
    pendingAttachmentsRef.current = pendingAttachments;
  }, [pendingAttachments]);

  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);

  useEffect(() => {
    stopSessionRef.current = stopSession;
  }, [stopSession]);

  useEffect(() => {
    // Once the list has caught up, the created thread resolves like any other thread.
    if (createdThreadIdRef.current && threads.some((thread) => thread.id === createdThreadIdRef.current)) {
      createdThreadIdRef.current = null;
    }
  }, [threads]);

  useEffect(() => {
    const previousBrandId = previousBrandIdRef.current;
    previousBrandIdRef.current = brandId;

    if (previousBrandId === brandId) {
      return;
    }

    if (previousBrandId) {
      newThreadCreationsByBrandRef.current.delete(previousBrandId);
    }
    initialRouteIntentHandled.current = "";
    newThreadRouteIntentClaimRef.current = "";
    createdThreadIdRef.current = null;
    beginThreadTransition({ recoverable: true });
    setActiveThreadId(null);
    setBriefContext(null);
    lastEnsuredBrandId.current = null;
  }, [brandId]);

  useEffect(() => {
    if (!shouldCreateNewThread) {
      newThreadRouteIntentClaimRef.current = "";
    }
  }, [shouldCreateNewThread]);

  useEffect(() => {
    if (!briefIdFromUrl || !brandId) return;

    setBriefContext({ id: briefIdFromUrl });
    setSearchParams((prev) => {
      prev.delete("briefId");
      return prev;
    }, { replace: true });
  }, [briefIdFromUrl, brandId, setSearchParams]);

  useEffect(() => {
    if (!brandId || lastEnsuredBrandId.current === brandId) {
      return;
    }
    lastEnsuredBrandId.current = brandId;
    void ensureRuntime(brandId).catch(() => {
      // Status route still reflects failure/degraded state; keep the UI soft here.
    });
  }, [brandId, ensureRuntime]);

  useEffect(() => {
    if (attachmentThreadRef.current === activeThreadId) {
      return;
    }

    if (attachmentThreadRef.current !== null) {
      setIsAttaching(false);
      setUploadQueue([]);
      attachmentSessionTokenRef.current += 1;
    }

    attachmentThreadRef.current = activeThreadId;
    const inflightEntry = getInflightAttachmentEntry(activeThreadId);
    const restoredAttachments = inflightEntry?.recoverable ? inflightEntry.attachments : [];
    setPendingAttachments(restoredAttachments);
    if (restoredAttachments.length > 0) {
      clearInflightAttachments(activeThreadId);
    }
  }, [activeThreadId]);

  useEffect(() => {
    autoScrollLockRef.current = true;
    armAutoScroll("auto", 1);
  }, [activeThreadId]);

  useEffect(() => {
    if (!shouldAutoScrollRef.current && !autoScrollLockRef.current) return;
    cancelAutoScrollFrame();
    autoScrollFrameRef.current = requestAnimationFrame(() => {
      autoScrollFrameRef.current = null;
      if (!shouldAutoScrollRef.current && !autoScrollLockRef.current) return;
      scrollToLatest(isStreaming || autoScrollLockRef.current ? "auto" : "smooth");
    });
    return cancelAutoScrollFrame;
  }, [messages.length, streamingText, isStreaming]);

  useEffect(() => {
    if (completedTurnCount === 0 || !shouldAutoScrollRef.current) {
      return;
    }
    autoScrollLockRef.current = true;
    armAutoScroll("auto", 4);
  }, [completedTurnCount]);

  useEffect(() => {
    const inflightAttachments = getInflightAttachments(activeThreadId);
    if (error && inflightAttachments.length > 0 && pendingAttachments.length === 0) {
      setPendingAttachments(inflightAttachments);
      clearInflightAttachments(activeThreadId);
    }
  }, [activeThreadId, error, pendingAttachments.length]);

  useEffect(() => {
    if (completedTurnCount === 0 || !lastCompletedThreadId) {
      return;
    }
    clearInflightAttachments(lastCompletedThreadId);
  }, [completedTurnCount, lastCompletedThreadId]);

  useEffect(() => cancelAutoScrollFrame, []);

  useEffect(() => {
    if (
      threadsLoading
      || !brandId
      || initialRouteIntentHandled.current === routeIntentKey
      || newThreadRouteIntentClaimRef.current === routeIntentKey
    ) {
      return;
    }

    async function resolveThreadSelection() {
      if (!brandId) return;

      if (shouldCreateNewThread) {
        if (newThreadCreationsByBrandRef.current.has(brandId)) return;
        const creationToken = Symbol(brandId);
        newThreadRouteIntentClaimRef.current = routeIntentKey;
        initialRouteIntentHandled.current = routeIntentKey;
        newThreadCreationsByBrandRef.current.set(brandId, creationToken);
        const previousThreadId = activeThreadIdRef.current;
        const previousPendingAttachments = pendingAttachmentsRef.current;
        try {
          beginThreadTransition({ recoverable: true });
          const thread = await createThread(brandId);
          if (!isCurrentNewThreadCreation(brandId, creationToken)) return;
          createdThreadIdRef.current = thread.id;
          setActiveThreadId(thread.id);
          setRouteError(null);
          initialRouteIntentHandled.current = `thread:${brandId}:${thread.id}`;
          navigate(`/agent/${brandId}?thread=${encodeURIComponent(thread.id)}`, { replace: true });
        } catch {
          if (!isCurrentNewThreadCreation(brandId, creationToken)) return;
          setRouteError("Could not start a new chat. Try again.");
          restoreThreadTransitionFailure(previousThreadId, previousPendingAttachments);
          setActiveThreadId((currentThreadId) => currentThreadId ?? threads[0]?.id ?? null);
          initialRouteIntentHandled.current = `default:${brandId}`;
          navigate(`/agent/${brandId}`, { replace: true });
        } finally {
          if (newThreadCreationsByBrandRef.current.get(brandId) === creationToken) {
            newThreadCreationsByBrandRef.current.delete(brandId);
          }
        }
        return;
      }

      if (requestedThreadId) {
        if (requestedThreadId !== activeThreadIdRef.current) {
          beginThreadTransition({ recoverable: true });
        }
        const resolvedThread = threads.find((thread) => thread.id === requestedThreadId)
          ?? (requestedThreadId === createdThreadIdRef.current ? { id: requestedThreadId } : null)
          ?? threads[0]
          ?? null;
        setActiveThreadId(resolvedThread?.id ?? null);
        setRouteError(null);
        initialRouteIntentHandled.current = resolvedThread && resolvedThread.id !== requestedThreadId
          ? `thread:${brandId}:${resolvedThread.id}`
          : routeIntentKey;
        if (resolvedThread && resolvedThread.id !== requestedThreadId) {
          navigate(`/agent/${brandId}?thread=${encodeURIComponent(resolvedThread.id)}`, { replace: true });
        }
        return;
      }

      if (activeThreadId) {
        initialRouteIntentHandled.current = routeIntentKey;
        return;
      }

      const firstThread = threads[0] ?? null;
      initialRouteIntentHandled.current = firstThread ? `thread:${brandId}:${firstThread.id}` : routeIntentKey;
      setActiveThreadId(firstThread?.id ?? null);
      if (firstThread) {
        navigate(`/agent/${brandId}?thread=${encodeURIComponent(firstThread.id)}`, { replace: true });
      }
    }

    void resolveThreadSelection();
  }, [
    activeThreadId,
    brandId,
    createThread,
    navigate,
    requestedThreadId,
    routeIntentKey,
    shouldCreateNewThread,
    threads,
    threadsLoading,
  ]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!brandId) return <Navigate to="/brands" replace />;

  async function handleNewThread() {
    if (!brandId) return;
    if (archivingThreadId) return;
    if (newThreadCreationsByBrandRef.current.has(brandId)) return;
    const creationToken = Symbol(brandId);
    newThreadCreationsByBrandRef.current.set(brandId, creationToken);
    const previousThreadId = activeThreadIdRef.current;
    const previousPendingAttachments = pendingAttachmentsRef.current;
    try {
      beginThreadTransition({ recoverable: true });
      const thread = await createThread(brandId);
      if (!isCurrentNewThreadCreation(brandId, creationToken)) return;
      createdThreadIdRef.current = thread.id;
      setActiveThreadId(thread.id);
      setRouteError(null);
      navigate(`/agent/${brandId}?thread=${encodeURIComponent(thread.id)}`, { replace: true });
    } catch {
      if (!isCurrentNewThreadCreation(brandId, creationToken)) return;
      setRouteError("Could not start a new chat. Try again.");
      restoreThreadTransitionFailure(previousThreadId, previousPendingAttachments);
      setActiveThreadId(previousThreadId);
    } finally {
      if (newThreadCreationsByBrandRef.current.get(brandId) === creationToken) {
        newThreadCreationsByBrandRef.current.delete(brandId);
      }
    }
  }

  function handleSelectThread(threadId: string) {
    if (archivingThreadId) return;
    if (threadId === activeThreadId) return;
    beginThreadTransition({ recoverable: true });
    setActiveThreadId(threadId);
  }

  async function handleArchiveThread(threadId: string) {
    if (!brandId || archivingThreadId) return;
    const previousThreadId = activeThreadIdRef.current;
    const previousPendingAttachments = pendingAttachmentsRef.current;
    try {
      if (threadId === previousThreadId) beginThreadTransition({ recoverable: true });
      await deleteThread(threadId);
      if (createdThreadIdRef.current === threadId) createdThreadIdRef.current = null;
      if (threadId !== previousThreadId) return;
      const nextThread = threads.find((thread) => thread.id !== threadId) ?? null;
      setActiveThreadId(nextThread?.id ?? null);
      setRouteError(null);
      clearError();
      if (!nextThread) navigate(`/agent/${brandId}`, { replace: true });
    } catch {
      setRouteError("Could not hide this chat. Try again.");
      if (threadId === previousThreadId) {
        restoreThreadTransitionFailure(previousThreadId, previousPendingAttachments);
        setActiveThreadId(previousThreadId);
      }
    }
  }

  function handleSend(content: string) {
    if (archivingThreadId) return;
    let messageToSend = content;

    if (briefContext && messages.filter((message) => message.role === "user").length === 0) {
      messageToSend = `Working on brief "${briefContext.title ?? briefContext.id}" (${briefContext.id}).\n\n${content}`;
      setBriefContext(null);
    }

    const attachmentsForTurn = pendingAttachments;
    if (attachmentsForTurn.length > 0) {
      const attachmentLines = attachmentsForTurn.map((attachment) =>
        attachment.url
          ? `- ${attachment.label}: ${attachment.url}`
          : `- ${attachment.label} (upload completed but preview URL is unavailable)`
      );
      messageToSend = `${messageToSend}\n\nAttached assets:\n${attachmentLines.join("\n")}`;
    }

    const accepted = sendMessage(messageToSend);
    if (!accepted) {
      return;
    }
    if (attachmentsForTurn.length > 0) {
      setInflightAttachments(activeThreadId, attachmentsForTurn);
      setPendingAttachments([]);
    }
    autoScrollLockRef.current = true;
    armAutoScroll("auto", 2);
  }

  async function handleAttachFiles(files: File[]) {
    if (archivingThreadId) return;
    if (!brandId || files.length === 0) return;
    setIsAttaching(true);
    const uploadBrandId = brandId;
    const uploadThreadId = activeThreadIdRef.current;
    const uploadSessionToken = attachmentSessionTokenRef.current;
    const isStaleAttachmentContext = () =>
      attachmentSessionTokenRef.current !== uploadSessionToken
      || brandIdRef.current !== uploadBrandId
      || activeThreadIdRef.current !== uploadThreadId;

    const acceptedFiles = files.filter((file) => ACCEPTED_CHAT_UPLOAD_MIME_TYPES.has(file.type));
    if (acceptedFiles.length === 0) {
      setAttachmentError("Chat attachments currently support PNG, JPEG, and WebP images.");
      setIsAttaching(false);
      return;
    }

    if (acceptedFiles.length !== files.length) {
      setAttachmentError("Some files were skipped because chat currently supports PNG, JPEG, and WebP images only.");
    } else {
      setAttachmentError(null);
    }

    try {
      const uploadedAssets: Array<{ queueId: string; id: string; label: string }> = [];

      for (const [index, file] of acceptedFiles.entries()) {
        if (isStaleAttachmentContext()) {
          break;
        }
        const formData = new FormData();
        formData.append("file", file);
        formData.append("label", file.name);
        const queueId = `upload:${Date.now()}:${index}:${file.name}`;
        setUploadQueue((current) => [
          ...current,
          {
            id: queueId,
            label: file.name,
            progress: 0,
            status: "uploading",
          },
        ]);
        try {
          const result = await uploadMedia.mutateAsync({
            formData,
            onProgress: (progress) => {
              if (isStaleAttachmentContext()) return;
              setUploadQueue((current) =>
                current.map((item) => item.id === queueId ? { ...item, progress } : item),
              );
            },
          });
          if (isStaleAttachmentContext()) {
            setUploadQueue((current) => current.filter((item) => item.id !== queueId));
            break;
          }
          const asset = result.data.created[0] ?? result.data.restored[0] ?? result.data.deduped[0];
          if (asset) {
            uploadedAssets.push({ queueId, id: asset.id, label: asset.label ?? file.name });
            setUploadQueue((current) =>
                current.map((item) => item.id === queueId ? { ...item, progress: 1, status: "processing" } : item),
              );
          } else {
            setUploadQueue((current) => current.filter((item) => item.id !== queueId));
            if (!isStaleAttachmentContext()) {
              setAttachmentError(`Upload for ${file.name} completed without a usable media asset. Please try again.`);
            }
          }
        } catch (error) {
          setUploadQueue((current) => current.filter((item) => item.id !== queueId));
          if (!isStaleAttachmentContext()) {
            setAttachmentError(error instanceof Error ? error.message : "One or more attachments failed to upload.");
          }
        }
      }

      if (uploadedAssets.length === 0) return;
      let linkById = new Map<string, string | null>();
      try {
        const links = await api.post<{ data: Array<{ asset_id: string; url: string | null; missing: boolean }> }>(
          `/api/brands/${uploadBrandId}/media/delivery-links`,
          {
            asset_ids: uploadedAssets.map((asset) => asset.id),
            purpose: "preview",
          },
        );
        if (isStaleAttachmentContext()) {
          setUploadQueue((current) =>
            current.filter((item) => !uploadedAssets.some((asset) => asset.queueId === item.id)),
          );
          return;
        }
        linkById = new Map(links.data.map((item) => [item.asset_id, item.url]));
      } catch (error) {
        if (isStaleAttachmentContext()) {
          setUploadQueue((current) =>
            current.filter((item) => !uploadedAssets.some((asset) => asset.queueId === item.id)),
          );
          return;
        }
        setAttachmentError(
          error instanceof Error
            ? `${error.message} Attachments still uploaded and can be sent without previews.`
            : "Attachments uploaded, but preview links could not be prepared.",
        );
      }
      setPendingAttachments((current) => [
        ...current,
        ...uploadedAssets.map((asset) => ({
          id: asset.id,
          label: asset.label,
          url: linkById.get(asset.id)
            ? new URL(linkById.get(asset.id)!, window.location.origin).toString()
            : null,
        })),
      ]);
      setUploadQueue((current) =>
        current.filter((item) => !uploadedAssets.some((asset) => asset.queueId === item.id)),
      );
    } catch (error) {
      if (isStaleAttachmentContext()) {
        return;
      }
      setAttachmentError(error instanceof Error ? error.message : "Attachment previews could not be prepared.");
    } finally {
      if (!isStaleAttachmentContext()) {
        setIsAttaching(false);
      }
    }
  }

  function handleRemoveAttachment(attachmentId: string) {
    setPendingAttachments((current) => current.filter((attachment) => attachment.id !== attachmentId));
  }

  function handleStopSession() {
    const inflightAttachments = getInflightAttachments(activeThreadId);
    if (inflightAttachments.length > 0 && pendingAttachments.length === 0) {
      setPendingAttachments(inflightAttachments);
      clearInflightAttachments(activeThreadId);
    }
    void stopSession();
  }

  const activeThread = threads.find((thread) => thread.id === activeThreadId) ?? null;
  const headerTitle = activeThread?.title?.trim() || "Operator thread";
  const composerPlaceholder = isStreaming
    ? "HelloConvo is drafting the next step..."
    : !activeThreadId
      ? threads.length === 0
        ? "Start a kickoff chat, then ask for the next marketing move."
        : "Choose a chat or start a new one to ask HelloConvo for the next step."
      : "Ask for the next draft, revision, or recommendation";
  const composerDisabledReason = archivingThreadId
    ? "Hiding this chat…"
    : isBrandStreamBusy
      ? "Another chat is completing a response. Return there or wait before sending."
    : !activeThreadId
      ? threadsLoading
        ? "Loading chat history…"
        : "Start a new chat before sending a message."
      : null;

  return (
    <WorkspaceContentFrame
      constrainToViewport
      mobileSecondaryLabel="Threads"
      secondary={!shellOwnsChatPanel ? (
        <AgentUIHistoryPanel
          threads={threads}
          activeThreadId={activeThreadId}
          archivingThreadId={archivingThreadId}
          brandName={brandName}
          onArchive={handleArchiveThread}
          onSelect={handleSelectThread}
          onNewThread={handleNewThread}
          isLoading={threadsLoading}
        />
      ) : null}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
        <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
          <header className="mx-auto flex w-full max-w-4xl items-start justify-between gap-4 px-6 pb-3 pt-6">
            <div className="min-w-0">
              <div className="font-mono text-sm text-slate-500">
                <span>{brandData?.data?.name ?? "Brand"}</span>
                <span className="px-2 text-slate-300">/</span>
                <span className="font-sans text-[15px] font-semibold text-slate-900">{headerTitle}</span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <RuntimeStatusBadge
                  runtime={runtimeStatusQuery.data?.data}
                  isLoading={runtimeStatusQuery.isLoading}
                  isEnsuring={isEnsuringRuntime}
                />
                {activeThread?.updated_at ? (
                  <span className="font-mono text-xs text-slate-400">Updated {timeAgo(activeThread.updated_at)}</span>
                ) : null}
                {briefContext ? (
                  <span className="font-mono text-xs text-slate-400">Brief attached</span>
                ) : null}
              </div>
            </div>
          </header>

          {(routeError || error || attachmentError) ? (
            <div className="mx-auto w-full max-w-4xl px-6 pb-4">
              <div
                role="alert"
                className="flex items-start justify-between gap-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
              >
                <div className="space-y-2">
                  <span>
                    {routeError
                      ?? attachmentError
                      ?? (error?.retryable
                        ? "Something went wrong. Wait a moment and try again."
                        : error?.message)}
                  </span>
                  {error?.retryable && error.message ? (
                    <p className="text-xs text-red-700">{error.message}</p>
                  ) : null}
                  {sessionStatus === "failed" || sessionStatus === "cancelled" ? (
                    <p className="text-xs text-red-700">Session ended. Start a new message.</p>
                  ) : null}
                </div>
                <div className="flex shrink-0 gap-2">
                  {error?.retryable ? (
                    <button
                      type="button"
                      disabled={sessionStatus === "running"}
                      onClick={() => {
                        retryLastMessage();
                      }}
                      className="rounded-full border border-red-200 bg-white px-3 py-1 text-xs font-medium text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Retry
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setRouteError(null);
                      setAttachmentError(null);
                      clearError();
                    }}
                    className="rounded-full border border-red-200 bg-white px-3 py-1 text-xs font-medium text-red-700 transition hover:bg-red-100"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          ) : null}
          {brandId ? (
            <div className="mx-auto w-full max-w-4xl px-6 pb-4">
              <BrandReadinessBanner brandId={brandId} missingOverride={brandNotReadyMissing} />
            </div>
          ) : null}
        </div>

        <div
          ref={scrollViewportRef}
          data-testid="message-scroll-viewport"
          className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto"
          onScroll={(event) => {
            if (suppressScrollObserverRef.current) {
              return;
            }
            const el = event.currentTarget;
            const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            const isNearBottom = distanceFromBottom < 120;
            shouldAutoScrollRef.current = isNearBottom;
            if (!isNearBottom) {
              autoScrollLockRef.current = false;
            }
          }}
        >
          <AgentUIMessageStream
            brandId={brandId ?? null}
            messages={messages}
            streamingText={streamingText}
            activeTool={activeTool}
            activityStatus={activityStatus}
            toolProgress={toolProgress}
            synthesisLearned={synthesisLearned}
            isStreaming={isStreaming}
            onPrimaryAction={!activeThreadId ? handleNewThread : null}
            primaryActionLabel={!activeThreadId ? (threads.length === 0 ? "Start kickoff chat" : "Start a new chat") : null}
          />
          <div ref={scrollBottomRef} className="h-1 w-full" />
        </div>

        <div className="sticky bottom-0 z-20 border-t border-slate-200 bg-white/95 backdrop-blur">
          <AgentUIComposer
            attachments={pendingAttachments}
            contextLabel={briefContext ? `Attached brief: ${briefContext.title ?? briefContext.id}` : null}
            disabledReason={composerDisabledReason}
            isUploadingAttachments={isAttaching || uploadQueue.length > 0}
            onSend={handleSend}
            onAttachFiles={handleAttachFiles}
            onRemoveAttachment={handleRemoveAttachment}
            onStop={handleStopSession}
            isStreaming={isStreaming}
            isStopping={isStopping}
            disabled={!activeThreadId || !!archivingThreadId || isBrandStreamBusy}
            initialValue={(location.state as { draft?: string } | null)?.draft}
            placeholder={composerPlaceholder}
            uploadQueue={uploadQueue}
          />
        </div>
      </div>
    </WorkspaceContentFrame>
  );
}
